import { useCallback, useMemo, useRef, useState } from 'react';

export type SortDir = 'asc' | 'desc';

export type SortValue = string | number | boolean | null | undefined;

/** Compare two cell values for client-side table sort. Null/empty last. */
export function compareSortValues(a: SortValue, b: SortValue): number {
  const aEmpty = a == null || a === '';
  const bEmpty = b == null || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (typeof a === 'number' && typeof b === 'number') {
    if (Number.isNaN(a) && Number.isNaN(b)) return 0;
    if (Number.isNaN(a)) return 1;
    if (Number.isNaN(b)) return -1;
    return a === b ? 0 : a < b ? -1 : 1;
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return a === b ? 0 : a ? 1 : -1;
  }
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Client-side multi-column sort for dashboard tables.
 *
 * Click a header via `toggle(key)`: first click asc, second desc, stays on
 * that column until another is chosen. `getValue` may be an unstable
 * inline function — it is read through a ref so re-renders do not thrash sort.
 */
export function useClientSort<T, K extends string = string>(
  rows: readonly T[],
  getValue: (row: T, key: K) => SortValue,
  defaults?: { key?: K; dir?: SortDir },
): {
  sorted: T[];
  sortKey: K | null;
  sortDir: SortDir;
  toggle: (key: K) => void;
} {
  const [sortKey, setSortKey] = useState<K | null>(defaults?.key ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(defaults?.dir ?? 'asc');

  const getValueRef = useRef(getValue);
  getValueRef.current = getValue;

  const toggle = useCallback((key: K) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return key;
    });
  }, []);

  const sorted = useMemo(() => {
    if (!sortKey) return rows.slice();
    const get = getValueRef.current;
    const dir = sortDir === 'asc' ? 1 : -1;
    const copy = rows.slice();
    copy.sort((a, b) => compareSortValues(get(a, sortKey), get(b, sortKey)) * dir);
    return copy;
  }, [rows, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggle };
}
